import { select, call, takeLatest, put } from 'redux-saga/effects';
import { ContractActions, InvoiceActions } from '../constants';
import { ContractApi, InvoiceApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteContractAction,
    GetContractAction,
    GetContractsAction,
    CreateContractAction,
    GetContractsDocsAction,
    PersistContractAction,
    getContracts,
    SendContractToDocusignAction, RemindContractToDocusignAction,
} from '../actions/contract-actions';
import sortingService from '../../services/sorting';
import {
    failureType,
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
    successType,
} from '../../services/api-handlers/api-resolver';
import { changeToZeroMoment, changeToZeroMomentDate } from '../../helpers/timeHelpers';
import { toast } from 'react-toastify';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { groupContract } from '../../helpers/contract/contract';
import { GetInvoicesAction } from '../actions/invoice-actions';

function* handleFetchContracts(action: GetContractsAction) {
    const oldData = yield select((state: StoreState) => state.contracts.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    let filterProps: any = { ...filters };
    if (filters?.createdAt) {
        filterProps = {
            ...filters,
            'createdAt[before]': changeToZeroMomentDate(`${filters.createdAt}`),
            'createdAt[after]': changeToZeroMomentDate(`${filters.createdAt}`),
        };
        delete filterProps.createdAt;
    }

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        ContractApi.getContracts({
            filters: filterProps,
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchContractsDocs(action: GetContractsDocsAction) {
    const oldData = yield select((state: StoreState) => state.contracts.dataDocs);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    const filterProps: any = { ...filters };

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        ContractApi.getContractsDocs({
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchContract(action: GetContractAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => ContractApi.getContract(id));
}

function* handleCreateContract(action: CreateContractAction) {
    const {
        payload: { data, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => ContractApi.createContract(data),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handleDeleteContract(action: DeleteContractAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => ContractApi.deleteContract(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistContract(action: PersistContractAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;
    const dataContract = groupContract(restData);
    //const dataContract = restData;
    yield resolveApiCall(
        action,
        () => ContractApi.createContract(dataContract),
        function* (data: ResolverApiSuccess) {
            //toast.success(`Admin work was successfully ${!id ? 'created' : 'updated'}.`);
            yield call(callbacks.resolve);
            yield put(push(Router.generate(routes.STUDENTS_LIST)));
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (result.error && result.error.response && result.error.response.status) {
                    toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handleSendContractToDocusign(action: SendContractToDocusignAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => ContractApi.sentToDocusign(id, restData),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);

            const actionContractFetch: GetContractAction = {
                type: ContractActions.FETCH_CONTRACT,
                payload: {
                    id: id,
                },
            };
            yield call(handleFetchContract, actionContractFetch);

            const actionContractsFetch: GetContractsAction = {
                type: ContractActions.FETCH_CONTRACTS,
                payload: {
                    page: 1,
                    itemsPerPage: 100000,
                    filters: {
                        'student.id': restData.student,
                        //...restData.filters,
                    },
                },
            };
            yield call(handleFetchContracts, actionContractsFetch);
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}


function* handleRemindContractToDocusign(action: RemindContractToDocusignAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => ContractApi.remindToDocusign(id, restData),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);

            const actionContractFetch: GetContractAction = {
                type: ContractActions.FETCH_CONTRACT,
                payload: {
                    id: id,
                },
            };
            yield call(handleFetchContract, actionContractFetch);

            const actionContractsFetch: GetContractsAction = {
                type: ContractActions.FETCH_CONTRACTS,
                payload: {
                    page: 1,
                    itemsPerPage: 100000,
                    filters: {
                        'student.id': restData.student,
                        //...restData.filters,
                    },
                },
            };
            yield call(handleFetchContracts, actionContractsFetch);
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                toast.error(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(ContractActions.FETCH_CONTRACTS, handleFetchContracts);
    yield takeLatest(ContractActions.FETCH_CONTRACT_DOCS, handleFetchContractsDocs);
    yield takeLatest(ContractActions.FETCH_CONTRACT, handleFetchContract);
    yield takeLatest(ContractActions.DELETE_CONTRACT, handleDeleteContract);
    yield takeLatest(ContractActions.CREATE_CONTRACT, handleCreateContract);
    yield takeLatest(ContractActions.PERSIST_CONTRACT, handlePersistContract);
    yield takeLatest(ContractActions.SEND_CONTRACT_TO_DOCUSIGN, handleSendContractToDocusign);
    yield takeLatest(ContractActions.REMIND_CONTRACT_TO_DOCUSIGN, handleRemindContractToDocusign);
}
